import { useRef, useState } from "react";
import { UploadCloud, Loader2, X } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

// Turns storage path into a fully qualified URL. Accepts either a full URL
// (leaves untouched) or a backend-relative path like /api/files/xxx.
export function resolveFileUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

export default function FileDropzone({
  value,
  onChange,
  accept = "image/*",
  label = "Upload image",
  testId = "file-dropzone",
  aspect = "aspect-square",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const resolved = resolveFileUrl(value);
  const isVideo = accept?.startsWith("video");

  return (
    <div
      data-testid={testId}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={`relative border-2 border-black bg-white ${aspect} overflow-hidden group cursor-pointer`}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {resolved ? (
        isVideo ? (
          <video src={resolved} className="w-full h-full object-cover" muted />
        ) : (
          <img src={resolved} alt="" className="w-full h-full object-cover" />
        )
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#0A0A0A]/50">
          <UploadCloud className="w-8 h-8" />
          <div className="font-mono text-xs uppercase tracking-widest">{label}</div>
          <div className="font-mono text-[10px] text-[#0A0A0A]/40">Click or drop</div>
        </div>
      )}
      {busy && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#F7931A]" />
        </div>
      )}
      {resolved && !busy && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange("");
          }}
          data-testid={`${testId}-clear`}
          className="absolute top-2 right-2 p-1 bg-white border-2 border-black opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Remove"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
